<template>
  <div v-if="show" class="modal-overlay" @click="closeModal">
    <div class="modal-content create-lead-modal" @click.stop>
      <div class="modal-header">
        <h2 class="modal-title">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" class="title-icon">
            <path d="M15,14C12.33,14 7,15.33 7,18V20H23V18C23,15.33 17.67,14 15,14M6,10V7H4V10H1V12H4V15H6V12H9V10M15,12A4,4 0 0,0 19,8A4,4 0 0,0 15,4A4,4 0 0,0 11,8A4,4 0 0,0 15,12Z"/>
          </svg>
          Create New Lead
        </h2>
        <button @click="closeModal" class="modal-close-btn">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z"/>
          </svg>
        </button>
      </div>

      <div class="modal-body">
        <!-- Loading State -->
        <div v-if="loading" class="loading-state">
          <div class="loading-spinner"></div>
          <p>Loading lead form...</p>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="error-state">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor" class="error-icon">
            <path d="M13,14H11V10H13M13,18H11V16H13M1,21H23L12,2L1,21Z"/>
          </svg>
          <p>{{ error }}</p>
          <button @click="loadRequiredFields" class="btn-retry">Retry</button>
        </div>

        <!-- Form -->
        <form v-else @submit.prevent="submitLead" class="lead-form">
          <!-- Success Message -->
          <div v-if="successMessage" class="success-message">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M9,20.42L2.79,14.21L5.62,11.38L9,14.77L18.88,4.88L21.71,7.71L9,20.42Z"/>
            </svg>
            {{ successMessage }}
          </div>

          <!-- Auto-filled Info Banner -->
          <div v-if="autoFilledFields.length > 0" class="auto-fill-banner">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M13,9H11V7H13M13,17H11V11H13M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z"/>
            </svg>
            <span>Auto-filled from WhatsApp: {{ autoFilledFields.join(', ') }}</span>
          </div>

          <!-- Dynamic Form Fields -->
          <div v-for="field in requiredFields" :key="field.lead_id" class="form-group">
            <label :for="field.column" class="form-label">
              {{ field.field_name }}
              <span class="required-star">*</span>
            </label>

            <!-- Text/Email/Tel/Number Inputs -->
            <input
              v-if="['text', 'email', 'tel', 'number'].includes(field.type)"
              :id="field.column"
              :type="field.type"
              v-model="formData[field.column]"
              :placeholder="field.placeholder"
              :disabled="field.column === 'phone_number'"
              :readonly="field.column === 'phone_number'"
              class="form-input"
              :class="{
                'auto-filled': isFieldAutoFilled(field.column),
                'read-only': field.column === 'phone_number'
              }"
            />

            <!-- Textarea -->
            <textarea
              v-else-if="field.type === 'textarea'"
              :id="field.column"
              v-model="formData[field.column]"
              :placeholder="field.placeholder"
              rows="3"
              class="form-input textarea-input"
            ></textarea>

            <!-- Select -->
            <select
              v-else-if="field.type === 'select'"
              :id="field.column"
              v-model="formData[field.column]"
              class="form-input"
            >
              <option value="" disabled>{{ field.placeholder }}</option>
              <option v-for="option in field.options" :key="option" :value="option">
                {{ option }}
              </option>
            </select>
          </div>

          <!-- Form Actions -->
          <div class="form-actions">
            <button type="button" @click="closeModal" class="btn-secondary" :disabled="saving">
              Cancel
            </button>
            <button type="submit" class="btn-primary" :disabled="saving || !isFormValid">
              <div v-if="saving" class="loading-spinner-small"></div>
              <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17,7H22V17H17V19A1,1 0 0,0 18,20H20V22H17.5C16.95,22 16,21.55 16,21C16,21.55 15.05,22 14.5,22H12V20H14A1,1 0 0,0 15,19V5A1,1 0 0,0 14,4H12V2H14.5C15.05,2 16,2.45 16,3C16,2.45 16.95,2 17.5,2H20V4H18A1,1 0 0,0 17,5V7M2,7H13V9H4V15H13V17H2V7M20,15V9H17V15H20Z"/>
              </svg>
              <span>{{ saving ? 'Saving...' : 'Save Lead' }}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CreateLeadModal',

  props: {
    show: {
      type: Boolean,
      default: false
    },
    // Pre-filled data from WhatsApp conversation
    phoneNumber: {
      type: String,
      default: ''
    },
    contactName: {
      type: String,
      default: ''
    }
  },

  data () {
    return {
      loading: false,
      saving: false,
      error: null,
      successMessage: '',
      requiredFields: [],
      formData: {},
      autoFilledFields: []
    }
  },

  computed: {
    isFormValid () {
      // Only require phone_number to be filled
      // User can save lead with partial information
      const phoneNumber = this.formData.phone_number
      return phoneNumber && phoneNumber.toString().trim().length > 0
    }
  },

  watch: {
    show (newVal) {
      if (newVal) {
        this.loadRequiredFields()
      } else {
        this.resetForm()
      }
    }
  },

  methods: {
    async loadRequiredFields () {
      this.loading = true
      this.error = null

      try {
        const token = localStorage.getItem('auth_token')

        // Load required fields
        const response = await fetch('https://api.quali-d.com/leads/required-fields', {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        const data = await response.json()

        if (!response.ok) {
          throw new Error(data.message || 'Failed to load required fields')
        }

        this.requiredFields = data.required_fields || []

        // Auto-fill phone number and name if provided
        this.autoFillData()

        // Load existing lead data if available
        if (this.phoneNumber) {
          await this.loadExistingLeadData()
        }
      } catch (err) {
        console.error('Error loading required fields:', err)
        this.error = err.message || 'Failed to load lead form. Please try again.'
      } finally {
        this.loading = false
      }
    },

    async loadExistingLeadData () {
      try {
        const token = localStorage.getItem('auth_token')
        const response = await fetch(`https://api.quali-d.com/collected-leads/${encodeURIComponent(this.phoneNumber)}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (response.ok) {
          const data = await response.json()

          if (data.lead_data) {
            // Populate form with existing lead data
            // Keep phone number from autoFillData (it's already set)
            // Override other fields with existing data if they exist
            Object.keys(data.lead_data).forEach(key => {
              if (data.lead_data[key] !== null && data.lead_data[key] !== '') {
                this.formData[key] = data.lead_data[key]
              }
            })
          }
        }
      } catch (err) {
        console.error('Error loading existing lead data:', err)
        // Don't throw error here - if existing data fails to load,
        // user can still create/update lead manually
      }
    },

    autoFillData () {
      this.autoFilledFields = []

      // Auto-fill phone number
      if (this.phoneNumber) {
        this.formData.phone_number = this.phoneNumber
        this.autoFilledFields.push('Phone Number')
      }

      // Auto-fill name if provided
      if (this.contactName && this.contactName !== 'Unknown Contact') {
        this.formData.name = this.contactName
        this.autoFilledFields.push('Name')
      }
    },

    isFieldAutoFilled (column) {
      // Only show auto-fill indicator for name (phone is always read-only)
      return (column === 'name' && this.contactName && this.contactName !== 'Unknown Contact')
    },

    async submitLead () {
      if (!this.isFormValid || this.saving) return

      this.saving = true
      this.error = null
      this.successMessage = ''

      try {
        const token = localStorage.getItem('auth_token')
        const response = await fetch('https://api.quali-d.com/leads/create-manual', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(this.formData)
        })

        const data = await response.json()

        if (!response.ok) {
          throw new Error(data.message || 'Failed to save lead')
        }

        this.successMessage = data.was_existing
          ? '✅ Lead updated successfully!'
          : '🎉 Lead created successfully!'

        // Emit success event
        this.$emit('lead-created', data)

        // Close modal after 1.5 seconds
        setTimeout(() => {
          this.closeModal()
        }, 1500)
      } catch (err) {
        console.error('Error saving lead:', err)
        this.error = err.message || 'Failed to save lead. Please try again.'
      } finally {
        this.saving = false
      }
    },

    closeModal () {
      this.$emit('close')
    },

    resetForm () {
      this.formData = {}
      this.autoFilledFields = []
      this.successMessage = ''
      this.error = null
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  backdrop-filter: blur(4px);
  animation: fadeIn 0.2s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.modal-content {
  background: var(--bg-primary);
  border-radius: 16px;
  box-shadow: 0 20px 60px var(--shadow-primary);
  max-width: 600px;
  width: 90%;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  animation: slideUp 0.3s ease-out;
  transition: all 0.3s ease;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px;
  border-bottom: 1px solid var(--border-primary);
}

.modal-title {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.title-icon {
  color: #10b981;
}

.modal-close-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  color: var(--text-secondary);
  transition: all 0.2s;
}

.modal-close-btn:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

.modal-body {
  flex: 1;
  padding: 24px;
  overflow-y: auto;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 64px 32px;
  color: var(--text-secondary);
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--border-primary);
  border-top: 3px solid #10b981;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Error State */
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 64px 32px;
  text-align: center;
}

.error-icon {
  color: #ef4444;
}

.error-state p {
  color: var(--text-secondary);
  margin: 0;
}

.btn-retry {
  padding: 10px 24px;
  background: #10b981;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-retry:hover {
  background: #059669;
  transform: translateY(-1px);
}

/* Success Message */
.success-message {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: rgba(16, 185, 129, 0.1);
  border: 1px solid rgba(16, 185, 129, 0.3);
  border-radius: 8px;
  color: #059669;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 16px;
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Auto-fill Banner */
.auto-fill-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.3);
  border-radius: 8px;
  color: #3b82f6;
  font-size: 13px;
  margin-bottom: 20px;
}

/* Form */
.lead-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-label {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}

.required-star {
  color: #ef4444;
  margin-left: 2px;
}

.form-input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid var(--border-secondary);
  border-radius: 8px;
  font-size: 14px;
  color: var(--text-primary);
  background: var(--bg-primary);
  transition: all 0.2s;
  box-sizing: border-box;
  font-family: inherit;
}

.form-input:focus {
  outline: none;
  border-color: #10b981;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
}

.form-input:disabled {
  background: var(--bg-tertiary);
  cursor: not-allowed;
  opacity: 0.7;
}

.form-input.auto-filled {
  background: rgba(59, 130, 246, 0.05);
  border-color: rgba(59, 130, 246, 0.3);
}

.form-input.read-only {
  background: var(--bg-tertiary);
  cursor: not-allowed;
  opacity: 0.8;
  font-weight: 500;
  border-color: var(--border-primary);
}

.textarea-input {
  resize: vertical;
  min-height: 80px;
}

/* Form Actions */
.form-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding-top: 8px;
  margin-top: 8px;
  border-top: 1px solid var(--border-primary);
}

.btn-secondary {
  padding: 12px 24px;
  background: var(--bg-primary);
  color: var(--text-primary);
  border: 1px solid var(--border-secondary);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-secondary:hover:not(:disabled) {
  background: var(--bg-tertiary);
  border-color: var(--text-tertiary);
}

.btn-secondary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #10b981;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary:hover:not(:disabled) {
  background: #059669;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.loading-spinner-small {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

/* Responsive */
@media (max-width: 768px) {
  .modal-content {
    width: 95%;
    max-height: 95vh;
  }

  .modal-header {
    padding: 20px;
  }

  .modal-body {
    padding: 20px;
  }

  .modal-title {
    font-size: 18px;
  }

  .form-actions {
    flex-direction: column-reverse;
  }

  .btn-primary,
  .btn-secondary {
    width: 100%;
    justify-content: center;
  }
}
</style>
